<script setup lang="ts">
import { ref } from 'vue'
import { BookOpen, FolderOpen, FolderSync, Trash2 } from '@lucide/vue'
import { Badge, Button, IconButton, InlineAlert, StateCard, useConfirm, useToast } from 'catalyst-vue'
import type { Library } from '../../db/schema'
import { useLibraryStore } from '../../stores/library'
import { useControlSize, useIconControlSize } from '../../lib/library-layout'
import { errorMessage } from '../../lib/fs-access'
import { useSourceRelocation } from '../../lib/source-relocation'
import LibraryOverlay from './library-overlay.vue'

defineProps<{ open: boolean }>()
defineEmits<{ close: [] }>()
const store = useLibraryStore()
const { confirm } = useConfirm()
const toast = useToast()
const controlSize = useControlSize()
const iconSize = useIconControlSize()
const error = ref('')
// 探測或寫入失敗留在對話框內講；選擇器要在點擊的同步堆疊內開啟，因此 relocate 直接掛在按鈕上。
const { relocating, relocate } = useSourceRelocation(message => { error.value = message })

async function remove(source: Library) {
  const count = store.comicsInSource(source.id).length
  await confirm({
    title: `移除來源「${source.name}」？`,
    description: count
      ? `這個來源底下的 ${count} 本作品會一併從書庫移除，包含它們的分類、閱讀進度與書籤。硬碟裡的檔案不會刪除，但重新加入後這些紀錄不會回來。`
      : '這個來源底下目前沒有作品。硬碟裡的檔案不會刪除。',
    tone: 'danger', confirmLabel: '移除來源', pendingLabel: '正在移除…', errorMessage,
    onConfirm: async () => {
      const removed = await store.removeSource(source.id)
      toast.success(`已移除來源「${source.name}」`, { description: removed ? `連同 ${removed} 本作品。` : undefined })
    },
  })
}
</script>

<template>
  <LibraryOverlay
    :open="open"
    size="2xl"
    title="管理來源"
    description="書庫從這些資料夾取得作品。移除來源只會清掉書庫裡的資料，硬碟上的檔案不受影響。"
    @close="$emit('close')"
  >
    <InlineAlert v-if="error" tone="error" class="mb-4">{{ error }}</InlineAlert>
    <StateCard v-if="!store.libraries.length" title="還沒有任何來源" message="用「加入作品」選一個漫畫根目錄，或直接匯入單獨一本作品的資料夾。" :actionable="false" />
    <ul v-else class="source-rows">
      <li v-for="source in store.libraries" :key="source.id">
        <component :is="source.kind === 'comic' ? BookOpen : FolderOpen" :size="17" aria-hidden="true" />
        <div class="source-row-text">
          <span class="source-row-name">
            {{ source.name }}
            <Badge :color="store.isAuthorized(source.id) ? 'success' : 'warning'">{{ store.isAuthorized(source.id) ? '已授權' : '未授權' }}</Badge>
          </span>
          <small>{{ source.kind === 'comic' ? '單一作品資料夾' : '根資料夾' }} · {{ store.comicsInSource(source.id).length }} 本</small>
        </div>
        <Button
          v-if="!store.isAuthorized(source.id)"
          outline
          :size="controlSize"
          :disabled="store.busy"
          @click="store.authorize(source.id)"
        >開啟</Button>
        <!-- 資料夾被搬走或改名時把來源指向新位置；識別碼與底下作品的資料都不動。接下來會出現瀏覽器自己的資料夾選擇視窗。 -->
        <IconButton
          :size="iconSize"
          class="source-row-remove"
          :disabled="store.busy || !store.supported"
          :label="relocating === source.id ? `正在核對「${source.name}」的新位置` : `重新選擇「${source.name}」的資料夾`"
          @click="error = ''; relocate(source)"
        ><FolderSync :size="16" aria-hidden="true" /></IconButton>
        <IconButton
          color="danger"
          :size="iconSize"
          class="source-row-remove"
          :disabled="store.busy"
          :label="`移除來源「${source.name}」`"
          @click="remove(source)"
        ><Trash2 :size="16" aria-hidden="true" /></IconButton>
      </li>
    </ul>
    <template #actions>
      <Button outline :size="controlSize" @click="$emit('close')">關閉</Button>
    </template>
  </LibraryOverlay>
</template>
